import { useEffect, useCallback, type MouseEvent } from 'react';
import { Helmet } from 'react-helmet-async';
import { useFormik } from 'formik';
import { useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, TextField, Typography, InputAdornment, Grid } from '@mui/material';

import { Test } from '../../models';
import { useAddTestMutation } from '../../slices/apis/testApi';

export default function NewTest() {
	const navigate = useNavigate();
	const [addTest, { isSuccess }] = useAddTestMutation();

	const refinedSchema = Test.schema.omit(['id', 'index']);

	const formik = useFormik({
		initialValues: refinedSchema.getDefault(),
		validationSchema: refinedSchema,
		onSubmit: async (values) => {
			await addTest(values);
		},
	});

	const handleNavigate = useCallback(
		(e: MouseEvent<HTMLButtonElement>) => {
			navigate(e.currentTarget.getAttribute('data-route') as string);
		},
		[navigate],
	);

	useEffect(() => {
		if (isSuccess) {
			navigate('/dashboard/admin/tests');
		}
	}, [navigate, isSuccess]);

	return (
		<>
			<Helmet>
				<title> New Test - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Grid container justifyContent="start">
				<Grid size={{ md: 6 }}>
					<Typography variant="h4" gutterBottom>
						New Test
					</Typography>
					<Card>
						<CardContent>
							<form onSubmit={formik.handleSubmit}>
								<Grid container spacing={2}>
									<Grid size={{ md: 12 }}>
										<TextField
											fullWidth
											margin="dense"
											id="name"
											name="name"
											label="Name"
											variant="standard"
											value={formik.values.name}
											onChange={formik.handleChange}
											error={formik.touched.name && Boolean(formik.errors.name)}
											helperText={formik.touched.name && formik.errors.name}
										/>
									</Grid>
									<Grid size={{ md: 12 }}>
										<TextField
											fullWidth
											margin="dense"
											id="desc"
											name="desc"
											label="Description"
											variant="standard"
											value={formik.values.desc}
											onChange={formik.handleChange}
											error={formik.touched.desc && Boolean(formik.errors.desc)}
											helperText={formik.touched.desc && formik.errors.desc}
										/>
									</Grid>
									<Grid size={{ md: 12 }}>
										<TextField
											fullWidth
											margin="dense"
											id="price"
											name="price"
											label="Price"
											variant="standard"
											type="number"
											value={formik.values.price}
											onChange={formik.handleChange}
											error={formik.touched.price && Boolean(formik.errors.price)}
											helperText={formik.touched.price && formik.errors.price}
											InputProps={{
												startAdornment: <InputAdornment position="start">$</InputAdornment>,
											}}
										/>
									</Grid>
									<Grid size={{ md: 4 }}>
										<Grid container justifyContent="start" spacing={4}>
											<Grid size={{ md: 6 }}>
												<Button loading={formik.isSubmitting} variant="outlined" color="primary" type="submit">
													<span>Save</span>
												</Button>
											</Grid>
											<Grid size={{ md: 6 }}>
												<Button
													color="error"
													variant="outlined"
													data-route="/dashboard/admin/tests"
													onClick={handleNavigate}
												>
													Cancel
												</Button>
											</Grid>
										</Grid>
									</Grid>
								</Grid>
							</form>
						</CardContent>
					</Card>
				</Grid>
			</Grid>
		</>
	);
}
